import React from "react";

const SAFE_SVG_TAGS = new Set([
  "svg",
  "path",
  "circle",
  "rect",
  "line",
  "polyline",
  "polygon",
  "ellipse",
  "g",
  "defs",
  "clipPath",
  "mask",
  "use",
  "title",
]);

export const convertToKebab = (spanString = "") => {
  const match = String(spanString).match(/>([\s\S]*?)</);
  const name = match ? match[1].trim() : "";
  return name.replace(/_/g, "-");
};

export const isSvgIconCode = (value = "") =>
  String(value).trim().toLowerCase().startsWith("<svg");

export const isGoogleIconCodePoint = (value = "") =>
  /^(?:0x|&#x|\\u)?[e-f][0-9a-f]{3};?$/i.test(String(value || "").trim());

const GOOGLE_ICON_CODEPOINT_NAMES = {
  e5ca: "check",
  e8b6: "search",
  e86c: "check_circle",
  e145: "add",
  e872: "delete",
  e3c9: "edit",
  e5cd: "close",
  e5c8: "arrow_forward",
  e5c4: "arrow_back",
  e5d2: "menu",
  e8f4: "visibility",
  e8f5: "visibility_off",
  e2c7: "upload_file",
  e226: "attach_file",
};

export const getGoogleIconCodePointHex = (value = "") => {
  const match = String(value || "")
    .trim()
    .match(/^(?:0x|&#x|\\u)?([e-f][0-9a-f]{3});?$/i);
  return match ? match[1].toLowerCase() : "";
};

export const getGoogleIconCodePoint = (value = "") => {
  const hex = getGoogleIconCodePointHex(value);
  return hex ? String.fromCodePoint(parseInt(hex, 16)) : "";
};

export const getGoogleIconNameFromCodePoint = (value = "") => {
  const hex = getGoogleIconCodePointHex(value);
  return hex ? GOOGLE_ICON_CODEPOINT_NAMES[hex] || "" : "";
};

export const getGoogleIconName = (value = "") => {
  const str = String(value || "").trim();
  if (!str || isSvgIconCode(str)) return "";
  if (isGoogleIconCodePoint(str)) return "";

  const htmlMatch = str.match(/material-symbols-outlined[^>]*>\s*([\s\S]*?)\s*</i);
  if (htmlMatch) return htmlMatch[1].trim();

  const queryMatch = str.match(/[?&]icon\.query=([a-z0-9_-]+)/i);
  const selectedMatch = str.match(/selected=[^:]*:([a-z0-9_-]+):/i);
  const pathMatch = str.match(/\/([a-z0-9_-]+)\s*$/i);
  const name = queryMatch?.[1] || selectedMatch?.[1] || pathMatch?.[1] || "";

  if (name) return name;
  return /^[a-z0-9_-]+$/i.test(str) ? str : "";
};

export const normalizeGoogleIconInput = (value = "") => {
  const str = String(value || "").trim();
  if (!str || isSvgIconCode(str)) return str;
  if (isGoogleIconCodePoint(str)) {
    return getGoogleIconNameFromCodePoint(str) || getGoogleIconCodePointHex(str);
  }

  const name = getGoogleIconName(str);
  return name ? name.trim().toLowerCase().replace(/[\s-]+/g, "_") : str;
};

export const sanitizeSvgIcon = (svg = "") => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(svg), "image/svg+xml");
  const svgNode = doc.querySelector("svg");
  if (!svgNode || doc.querySelector("parsererror")) return "";

  Array.from(svgNode.querySelectorAll("*")).forEach((node) => {
    if (!SAFE_SVG_TAGS.has(node.tagName)) {
      node.remove();
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      const unsafeHref =
        (name === "href" || name === "xlink:href") &&
        value &&
        !value.startsWith("#");

      if (name.startsWith("on") || name === "style" || unsafeHref) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return svgNode.outerHTML;
};

export const PackageIcon = ({ iconHtml, style, className = "", title = "Icon" }) => {
  const str = String(iconHtml || "").trim();
  if (!str) return null;

  if (isSvgIconCode(str)) {
    const sanitizedSvg = sanitizeSvgIcon(str);
    if (!sanitizedSvg) return null;

    return (
      <span
        style={{ display: "inline-flex", alignItems: "center", ...style }}
        className={className}
        aria-label={title}
        dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
      />
    );
  }

  const name = getGoogleIconName(str);
  const codePointName = getGoogleIconNameFromCodePoint(str);
  const codePoint = getGoogleIconCodePoint(str);
  if (!name && !codePoint) return null;

  return (
    <span
      className={`material-symbols-outlined${className ? ` ${className}` : ""}`}
      style={style}
      aria-label={title}
    >
      {codePointName || codePoint || name}
    </span>
  );
};
